import { useState, useMemo, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import globalConfig from '../../config/globalConfig';

const useStyles = makeStyles((theme) => ({
  '5xlarge': {
    width: theme.spacing(40),
    height: theme.spacing(40),
    fontSize: '15em'
  },
  '4xlarge': {
    width: theme.spacing(35),
    height: theme.spacing(35),
    fontSize: '10em'
  },
  xlarge: {
    width: theme.spacing(20),
    height: theme.spacing(20),
    fontSize: '8.5em'
  },
  large: {
    width: theme.spacing(15),
    height: theme.spacing(15),
    fontSize: '7.5em'
  },
  xmedium: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    fontSize: '5em'
  },
  medium: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    fontSize: '3.5em'
  },
  dashboard: {
    width: theme.spacing(6),
    height: theme.spacing(6),
    fontSize: '2.5em'
  },
  chart: {
    width: theme.spacing(5.5),
    height: theme.spacing(5.5),
    fontSize: '2em'
  },
  small: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    fontSize: '1.5em'
  },
  xsmall: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    fontSize: '0.8em'
  }
}));

const variantToPixels = {
  circular: '50%',
  rounded: '4px',
  square: '0'
};

const srcAvatar = (tenantId, src) => {
  return src ? `${globalConfig.storage}/tenants/${tenantId}/${src}` : null;
};

/*  CustomAvatar
 *  @params src             string    source photo
 *  @params showButton      bool      show button upload
 */

const CustomAvatar = ({
  src,
  srcComplete,
  variant,
  size = 'large',
  alt = '',
  showCompleteAlt = false,
  style
}) => {
  const classes = useStyles();
  const tenantId = useSelector((state) => state?.tenant?.tenant?.tenantId || null);
  const [photoExists, setPhotoExists] = useState(false);

  useEffect(() => {
    if (src) {
      const checkImage = (path) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
  
          img.src = path;
        });
      checkImage(srcComplete ? srcComplete : srcAvatar(tenantId, src)).then((response) => {
        setPhotoExists(response);
      });
    }
    
  }, [src, srcComplete]);

  let name = src ? src.split('/') : [];
  const borderRadius = useMemo(() => {
    return variantToPixels[variant] || variantToPixels.rounded;
  }, [variant]);

  const imageUri = useMemo(() => {
    return srcComplete ? srcComplete : srcAvatar(tenantId, src);
  }, [src, srcComplete]);

  if (!src) {
    if (alt) {
      return (
        <Avatar variant={variant} style={style} className={classes[size]}>
          {showCompleteAlt ? alt : alt.charAt(0)}
        </Avatar>
      );
    } else {
      return <Avatar variant={variant} style={style} className={classes[size]}></Avatar>;
    }
  }

  if (name.length > 0 && photoExists) {
    name = name[name.length - 1];
    if (name !== '') {
      return (
        <Avatar alt={alt} style={style} variant={variant} className={classes[size]}>
          <div
            style={{
              ...style,
              borderRadius,
              backgroundImage: `url("${imageUri}")`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover'
            }}
            className={classes[size]}
          />
        </Avatar>
      );
    }
  }
  if (alt) {
    return (
      <Avatar variant={variant} style={style} className={classes[size]}>
        {showCompleteAlt ? alt : alt.charAt(0)}
      </Avatar>
    );
  }

  return <Avatar variant={variant} style={style} className={classes[size]}></Avatar>;
};

export default CustomAvatar;
